package com.gastoncantera.intercom

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.gastoncantera.intercom.auth.AuthRepository
import com.gastoncantera.intercom.auth.LoginScreen
import com.gastoncantera.intercom.auth.WelcomeDialog
import com.gastoncantera.intercom.auth.WelcomeStore
import com.gastoncantera.intercom.call.CallRepository
import com.gastoncantera.intercom.config.AppConfig
import com.gastoncantera.intercom.config.ConfigRepository
import com.gastoncantera.intercom.config.UserRole
import com.gastoncantera.intercom.config.resolveRole
import com.gastoncantera.intercom.presence.PresenceRepository
import com.gastoncantera.intercom.presence.signOutReleasingPush
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.FirebaseFirestoreException
import dev.gitlive.firebase.firestore.FirestoreExceptionCode
import dev.gitlive.firebase.firestore.code
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.time.Duration.Companion.hours

private sealed interface ConfigState {
    data object Loading : ConfigState
    data class Error(val message: String) : ConfigState
    data class Loaded(val config: AppConfig, val webQrEnabled: Boolean) : ConfigState
}

/**
 * Por qué no se pudo leer el doc del usuario al arrancar.
 *
 * [NotEnabled] = las reglas rechazaron el acceso: el uid todavía no está en las allowlists de
 * firestore.rules (alta a medio hacer). No se reintenta, hay que darlo de alta.
 */
private enum class AccessError { NotEnabled, Unreachable }

/**
 * Cada cuánto se relee la config mientras la app vive.
 *
 * Existe por el portero: es un kiosco encendido y sin nadie que lo atienda, así que si la config se
 * leyera solo al arrancar, publicar un depto nuevo no llegaría nunca a su pantalla. El residente
 * también se beneficia (empieza a recibir las llamadas del depto nuevo sin reiniciar), pero para él
 * el reinicio siempre fue una salida aceptable.
 *
 * Una vez por hora está lejos del throttling de Remote Config (estrangula arriba de unos 5 fetch por
 * hora) y alcanza de sobra para algo planificado y poco frecuente como dar de alta un depto.
 */
private val CONFIG_REFRESH_INTERVAL = 1.hours

@Composable
fun App() {
    MaterialTheme {
        val authRepository = remember { AuthRepository() }
        val presenceRepository = remember { PresenceRepository() }
        val callRepository = remember { CallRepository() }
        val configRepository = remember { ConfigRepository() }

        var configState by remember { mutableStateOf<ConfigState>(ConfigState.Loading) }
        var attempt by remember { mutableIntStateOf(0) }

        LaunchedEffect(attempt) {
            configState = ConfigState.Loading
            configState = try {
                val settings = configRepository.loadSettings()
                val qrUid = settings.config.resolveQrPorteroUid(settings.webQrEnabled)
                // Web: gate de ubicación del consorcio (si QR activo). En Android/iOS es no-op.
                if (qrUid != null) {
                    enforceLocationGate(settings.config.locationGateTargetsForPortero(qrUid))
                }
                ConfigState.Loaded(settings.config, settings.webQrEnabled)
            } catch (e: Throwable) {
                ConfigState.Error(e.message ?: "No se pudo cargar la configuración")
            }
        }

        // Relectura periódica (ver [CONFIG_REFRESH_INTERVAL]). Solo pisa una config ya cargada: si el
        // arranque falló, el camino sigue siendo Reintentar, que es el único que corre el gate de
        // ubicación. Y no repite ese gate acá a propósito: en modo QR se revalida en cada llamada, y
        // pedir la ubicación una vez por hora sería un permiso pidiéndose solo.
        LaunchedEffect(Unit) {
            while (true) {
                delay(CONFIG_REFRESH_INTERVAL)
                if (configState !is ConfigState.Loaded) continue
                val settings = runCatching { configRepository.loadSettings() }.getOrNull() ?: continue
                // Si no cambió nada esto no recompone nada: Loaded y todo el dominio son data class,
                // así que asignar un valor igual al actual es un no-op para Compose.
                configState = ConfigState.Loaded(settings.config, settings.webQrEnabled)
            }
        }

        when (val state = configState) {
            is ConfigState.Loading -> CenteredMessage { CircularProgressIndicator() }

            is ConfigState.Error -> CenteredMessage {
                Text("Error cargando configuración", style = MaterialTheme.typography.titleMedium)
                Text(
                    state.message,
                    modifier = Modifier.padding(top = 8.dp),
                    textAlign = TextAlign.Center,
                )
                Button(onClick = { attempt++ }, modifier = Modifier.padding(top = 16.dp)) {
                    Text("Reintentar")
                }
            }

            is ConfigState.Loaded -> {
                val config = state.config
                // Web (QR): /<uid> + web_qr_enabled + ubicacion en el consorcio → auth anónima.
                // Si el kill switch global está off, falta radio en ubicacion, o no es portero válido
                // (o Android/iOS), se sigue el flujo normal de login como en nativas.
                val qrPorteroUid = remember(config, state.webQrEnabled) {
                    config.resolveQrPorteroUid(state.webQrEnabled)
                }

                // initial desde disco: evita Login falso al reabrir con sesión restaurada.
                val user by authRepository.currentUser.collectAsState(
                    initial = authRepository.currentUserOrNull(),
                )
                val currentUser = user

                // Una sesión restaurada implica que esta instalación ya pasó por el login. Esto
                // evita mostrar la bienvenida por primera vez recién cuando un usuario existente
                // cierra sesión después de actualizar la app.
                LaunchedEffect(currentUser?.uid) {
                    if (
                        currentUser != null &&
                        WelcomeStore.supported &&
                        !WelcomeStore.accepted
                    ) {
                        WelcomeStore.accepted = true
                    }
                }

                if (qrPorteroUid != null) {
                    LaunchedEffect(currentUser == null) {
                        if (currentUser == null) authRepository.signInAnonymously()
                    }
                    if (currentUser == null) {
                        CenteredMessage { CircularProgressIndicator() }
                    } else {
                        val role = config.resolveRole(qrPorteroUid) as UserRole.Portero
                        PorteroScreen(
                            porteroUid = qrPorteroUid,
                            consorcios = role.consorcios,
                            authRepository = null,
                            presenceRepository = presenceRepository,
                            callRepository = callRepository,
                        )
                    }
                } else if (currentUser == null) {
                    LoginScreen(authRepository)
                    var showWelcome by remember {
                        mutableStateOf(WelcomeStore.supported && !WelcomeStore.accepted)
                    }
                    if (showWelcome) {
                        WelcomeDialog(
                            authRepository = authRepository,
                            onAccepted = {
                                WelcomeStore.accepted = true
                                showWelcome = false
                            },
                        )
                    }
                } else {
                    val scope = rememberCoroutineScope()
                    var error by remember(currentUser.uid) { mutableStateOf<AccessError?>(null) }
                    var ready by remember(currentUser.uid) { mutableStateOf(false) }
                    var accessAttempt by remember(currentUser.uid) { mutableIntStateOf(0) }
                    LaunchedEffect(currentUser.uid, accessAttempt) {
                        error = null
                        try {
                            Firebase.firestore.collection("users").document(currentUser.uid).get()
                            ready = true
                        } catch (e: Throwable) {
                            error = if (e.isPermissionDenied()) {
                                AccessError.NotEnabled
                            } else {
                                AccessError.Unreachable
                            }
                        }
                    }
                    val accessError = error
                    if (accessError != null) {
                        AccessErrorDialog(
                            error = accessError,
                            onRetry = { accessAttempt++ },
                            onSignOut = {
                                scope.launch {
                                    signOutReleasingPush(
                                        authRepository,
                                        presenceRepository,
                                        currentUser.uid,
                                    )
                                }
                            },
                        )
                    } else if (!ready) {
                        CenteredMessage { CircularProgressIndicator() }
                    } else when (val role = config.resolveRole(currentUser.uid)) {
                        is UserRole.Portero -> PorteroScreen(
                            porteroUid = currentUser.uid,
                            consorcios = role.consorcios,
                            authRepository = authRepository,
                            presenceRepository = presenceRepository,
                            callRepository = callRepository,
                        )

                        is UserRole.Usuario -> UsuarioScreen(
                            user = currentUser,
                            matches = role.matches,
                            authRepository = authRepository,
                            presenceRepository = presenceRepository,
                            callRepository = callRepository,
                        )

                        UserRole.None -> NoAccessScreen(
                            authRepository = authRepository,
                            presenceRepository = presenceRepository,
                            uid = currentUser.uid,
                        )
                    }
                }
            }
        }
    }
}

/**
 * UID de portero para modo QR web, o null → flujo nativo (login).
 *
 * Requiere: [webQrEnabled], path `/<uid>`, rol Portero, y al menos un consorcio con
 * `ubicacion` `lat,lng,radio`. Sin radio = kill switch por portero.
 */
private fun AppConfig.resolveQrPorteroUid(webQrEnabled: Boolean): String? {
    if (!webQrEnabled) return null
    val uid = startupPorteroUid() ?: return null
    if (resolveRole(uid) !is UserRole.Portero) return null
    val delPortero = consorcios.filter { it.porteroUid == uid }
    if (delPortero.locationGateTargets().isEmpty()) return null
    return uid
}

private fun AppConfig.locationGateTargetsForPortero(porteroUid: String): List<LocationGateTarget> =
    consorcios.filter { it.porteroUid == porteroUid }.locationGateTargets()

private fun Throwable.isPermissionDenied(): Boolean =
    this is FirebaseFirestoreException && code == FirestoreExceptionCode.PERMISSION_DENIED

@Composable
private fun AccessErrorDialog(
    error: AccessError,
    onRetry: () -> Unit,
    onSignOut: () -> Unit,
) {
    val notEnabled = error == AccessError.NotEnabled
    AlertDialog(
        onDismissRequest = {},
        title = { Text(if (notEnabled) "Cuenta sin habilitar" else "Sin conexión") },
        text = {
            Text(
                if (notEnabled) {
                    "Tu cuenta todavía no tiene acceso. Pedile al administrador que la habilite."
                } else {
                    "No pudimos verificar tu cuenta. Revisá tu conexión e intentá de nuevo."
                },
            )
        },
        confirmButton = {
            if (notEnabled) {
                TextButton(onClick = onSignOut) { Text("Cerrar sesión") }
            } else {
                TextButton(onClick = onRetry) { Text("Reintentar") }
            }
        },
        dismissButton = {
            if (!notEnabled) {
                TextButton(onClick = onSignOut) { Text("Cerrar sesión") }
            }
        },
    )
}

@Composable
private fun NoAccessScreen(
    authRepository: AuthRepository,
    presenceRepository: PresenceRepository,
    uid: String,
) {
    val scope = rememberCoroutineScope()
    CenteredMessage {
        Text("Sin acceso", style = MaterialTheme.typography.titleMedium)
        Text(
            "Tu usuario no está asignado a ningún consorcio.",
            modifier = Modifier.padding(top = 8.dp),
            textAlign = TextAlign.Center,
        )
        Button(
            onClick = {
                scope.launch { signOutReleasingPush(authRepository, presenceRepository, uid) }
            },
            modifier = Modifier.padding(top = 16.dp),
        ) {
            Text("Cerrar sesión")
        }
    }
}

@Composable
private fun CenteredMessage(content: @Composable () -> Unit) {
    Column(
        modifier = Modifier.fillMaxSize()
            .windowInsetsPadding(WindowInsets.safeDrawing)
            .padding(24.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        content()
    }
}
